import { useState, useEffect, useCallback, useRef } from 'react'
import { BlogMetadataRecord } from 'api/clients/BlogMetadataRecordsClient'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useModals } from 'almanac-ui/Modal'

export interface FeaturedImageData {
  url: string
  alt_text: string
}

export interface UseBlogMetadataImagesOptions {
  initialData?: BlogMetadataRecord
  onImageUpdate?: (featuredMedia: Partial<BlogMetadataRecord['featured_media']>) => void
}

export interface BlogMetadataImagesState {
  featuredImageUrl: string
  featuredImageAltText: string
  featuredImages: string[]
  imageLoading: boolean
  imageError: boolean
  hasExistingImages: boolean
}

export interface FeaturedMediaModalProps {
  images: string[]
  selectedImage?: string
  onSelect: (image: string) => void
  onClose: () => void
  onImageUpload: (url: string) => void
}

export interface BlogMetadataImagesHandlers {
  handleFeaturedImageAltChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleDirectUpload: () => void
  handleFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleOpenFeaturedMediaModal: (
    FeaturedMediaSelectModalComponent: React.FunctionComponent<FeaturedMediaModalProps>,
  ) => void
  setImageLoading: (loading: boolean) => void
  setImageError: (error: boolean) => void
}

export interface BlogMetadataImagesRefs {
  fileInputRef: React.RefObject<HTMLInputElement>
}

export interface UseBlogMetadataImagesReturn {
  imageState: BlogMetadataImagesState
  handlers: BlogMetadataImagesHandlers
  refs: BlogMetadataImagesRefs
}

export const useBlogMetadataImages = ({
  initialData,
  onImageUpdate,
}: UseBlogMetadataImagesOptions): UseBlogMetadataImagesReturn => {
  const { openModal, closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Image state
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)
  const [featuredImageUrl, setFeaturedImageUrl] = useState<string>(
    initialData?.featured_media?.current_featured_image ||
      initialData?.featured_media?.featured_selected_images?.[0]?.url ||
      '',
  )
  const [featuredImageAltText, setFeaturedImageAltText] = useState<string>(() => {
    // Get initial alt text from the current featured image
    const selectedImages = initialData?.featured_media?.featured_selected_images || []
    const currentImage = selectedImages.find(
      (img) => img.url === (initialData?.featured_media?.current_featured_image || selectedImages[0]?.url),
    )
    return currentImage?.alt_text || initialData?.featured_media?.alt_text || ''
  })
  const [featuredImages, setFeaturedImages] = useState<string[]>(
    initialData?.featured_media?.featured_selected_images?.map((img) => img.url) ?? [],
  )

  // Upload handling with useImportMediaFiles
  const { uploadImagesFromEvent } = useImportMediaFiles(false, (files) => {
    if (files && files.length > 0 && files[0].source_url) {
      const newImageUrl = files[0].source_url

      // Add to featured images array
      const newImages = [...featuredImages, newImageUrl]
      setFeaturedImages(newImages)

      // Create new selected images array
      const newSelectedImages = newImages.map((url) => ({ url, alt_text: '' }))

      // Set as current featured image and update metadata
      const updatedFeaturedMedia = {
        ...initialData?.featured_media,
        featured_selected_images: newSelectedImages,
        current_featured_image: newImageUrl,
      }

      onImageUpdate?.(updatedFeaturedMedia)

      // Update local state
      setFeaturedImageUrl(newImageUrl)
      setFeaturedImageAltText('')
    }
  })

  // Helper functions
  const getAltTextForImage = useCallback(
    (imageUrl: string): string => {
      const selectedImages = initialData?.featured_media?.featured_selected_images || []
      const imageObj = selectedImages.find((img) => img.url === imageUrl)
      return imageObj?.alt_text || ''
    },
    [initialData?.featured_media?.featured_selected_images],
  )

  const updateAltTextForCurrentImage = useCallback(
    (imageUrl: string) => {
      const altText = getAltTextForImage(imageUrl)
      setFeaturedImageAltText(altText)
    },
    [getAltTextForImage],
  )

  // Handlers
  const handleDirectUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!uploadsProjectId) return

      if (event.target.files && event.target.files.length > 1) {
        const dt = new DataTransfer()
        dt.items.add(event.target.files[0])
        event.target.files = dt.files
      }

      uploadImagesFromEvent(event, uploadsProjectId)

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [uploadsProjectId, uploadImagesFromEvent],
  )

  const handleFeaturedImageAltChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFeaturedImageAlt = e.target.value
      setFeaturedImageAltText(newFeaturedImageAlt)

      const selectedImages = initialData?.featured_media?.featured_selected_images || []
      const updatedSelectedImages = selectedImages.map((img) => {
        return img.url === featuredImageUrl ? { ...img, alt_text: newFeaturedImageAlt } : img
      })

      const updatedFeaturedMedia = {
        ...initialData?.featured_media,
        alt_text: newFeaturedImageAlt,
        featured_selected_images: updatedSelectedImages,
      }

      onImageUpdate?.(updatedFeaturedMedia)
    },
    [featuredImageUrl, initialData?.featured_media, onImageUpdate],
  )

  const handleOpenFeaturedMediaModal = useCallback(
    (FeaturedMediaSelectModalComponent: React.FunctionComponent<FeaturedMediaModalProps>) => {
      openModal(FeaturedMediaSelectModalComponent, {
        images: featuredImages,
        selectedImage: featuredImageUrl,
        onSelect: (img: string) => {
          // Only add the image if it's not already in the array
          if (!featuredImages.includes(img)) {
            const newImages = [...featuredImages, img]
            const existingSelectedImages = initialData?.featured_media?.featured_selected_images || []

            // Create new selected images array, preserving existing alt text
            const newSelectedImages = newImages.map((url) => {
              const existingImage = existingSelectedImages.find((imgObj) => imgObj.url === url)
              return existingImage || { url, alt_text: '' }
            })

            const updatedFeaturedMedia = {
              ...initialData?.featured_media,
              featured_selected_images: newSelectedImages,
              current_featured_image: img,
            }

            onImageUpdate?.(updatedFeaturedMedia)
          } else {
            // Image already exists, just update the current featured image
            const updatedFeaturedMedia = {
              ...initialData?.featured_media,
              current_featured_image: img,
            }

            onImageUpdate?.(updatedFeaturedMedia)
          }

          setFeaturedImageUrl(img)
          updateAltTextForCurrentImage(img)
          closeModal()
        },
        onClose: closeModal,
        onImageUpload: (imgUrl: string) => {
          // Only add the image if it's not already in the array
          if (!featuredImages.includes(imgUrl)) {
            const newImages = [...featuredImages, imgUrl]
            setFeaturedImages(newImages)

            const existingSelectedImages = initialData?.featured_media?.featured_selected_images || []

            // Create new selected images array, preserving existing alt text
            const newSelectedImages = newImages.map((url) => {
              const existingImage = existingSelectedImages.find((imgObj) => imgObj.url === url)
              return existingImage || { url, alt_text: '' }
            })

            // Update metadata with the new images array - don't automatically set as current
            const updatedFeaturedMedia = {
              ...initialData?.featured_media,
              featured_selected_images: newSelectedImages,
            }

            onImageUpdate?.(updatedFeaturedMedia)
          }
        },
      })
    },
    [
      featuredImages,
      featuredImageUrl,
      initialData?.featured_media,
      onImageUpdate,
      updateAltTextForCurrentImage,
      openModal,
      closeModal,
    ],
  )

  // Sync state with initialData changes
  useEffect(() => {
    if (initialData?.featured_media?.current_featured_image) {
      setFeaturedImageUrl(initialData.featured_media.current_featured_image)
    }
  }, [initialData?.featured_media?.current_featured_image])

  useEffect(() => {
    if (initialData?.featured_media?.featured_selected_images) {
      setFeaturedImages(initialData.featured_media.featured_selected_images.map((img) => img.url))
    }
  }, [initialData?.featured_media?.featured_selected_images])

  return {
    imageState: {
      featuredImageUrl,
      featuredImageAltText,
      featuredImages,
      imageLoading,
      imageError,
      hasExistingImages: featuredImages.length > 0,
    },
    handlers: {
      handleFeaturedImageAltChange,
      handleDirectUpload,
      handleFileInputChange,
      handleOpenFeaturedMediaModal,
      setImageLoading,
      setImageError,
    },
    refs: {
      fileInputRef,
    },
  }
}
